import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonIcon,
  IonText,
  IonSpinner,
} from '@ionic/react';
import { useFirmwareUpdate } from 'components/useUpdateOverlay';
import { bluetoothOutline, bugOutline } from 'ionicons/icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useAuthService } from 'services/Authentication';
import Trans from 'util/Trans';
import { StyledProps } from '../../../components';
import HStack from '../../../components/HStack';
import { BluetoothInfo } from '../../../models/BluetoothInfo';
import { StatesBLE } from '../../../services/BLE/BLE';
import { NAME_FREAK, NAME_FREAK_DISPLAY } from '../../../spinnaxBase/constants';
import { logger } from 'services/Logger';

interface SensorCardProps extends StyledProps {
  device: BluetoothInfo;
  batteryLevel?: number;
  isConnecting?: boolean;
  isConnected?: boolean;
  isSelected?: boolean;
  info?: any; // Firmware-Info explizit als Prop
  highlightYellow?: boolean;
}

const SensorCard = observer((props: SensorCardProps) => {
  const { device, batteryLevel, isConnecting, isConnected, isSelected, info, highlightYellow } = props;
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const auth = useAuthService();
  const { checkForUpdates } = useFirmwareUpdate();
  const token = auth.userToken.get();
  useEffect(() => {
    if (!device.info || !token) return;
    // Outcommented to prevent automatic update checks as long as the process fails
    // checkForUpdates({ updateCB: setUpdateAvailable, failSilent: true });
  }, [device.entry?.id, device.info, checkForUpdates, token]);

  const firmwareLine = () => {
    const firmwareInfo = info || device.info;
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto max-content',
          alignItems: 'end',
        }}
      >
        <IonCardSubtitle style={{ color: 'var(--ion-text-color)' }}>
          {device.entry!.mac && `${device.entry!.mac}`}
        </IonCardSubtitle>

        <div
          style={{
            width: '100%',
            alignItems: 'flex-end',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={() => {
            updateAvailable &&
              checkForUpdates({ updateCB: setUpdateAvailable });
          }}
        >
          <IonIcon
            slot="end"
            size="large"
            color="light"
            src="/assets/icons/refresh.svg"
            style={{
              visibility: updateAvailable ? 'visible' : 'hidden',
            }}
          />

          <IonText style={{ color: 'var(--ion-text-color)' }}>
            {firmwareInfo?.firmwareVersion &&
              `v ${firmwareInfo?.firmwareVersion}`}
          </IonText>
        </div>
      </div>
    );
  };

  // Hintergrundfarbe je nach Status
  let cardBg: string | undefined = undefined;
  if (isConnected) cardBg = 'var(--ion-color-success)';
  else if (highlightYellow) cardBg = 'var(--ion-color-warning)';
  else if (isSelected) cardBg = 'var(--ion-color-secondary)';

  return (
    <IonCard style={{ margin: '8px', position: 'relative', background: cardBg }}>
      {isConnecting && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            borderRadius: 8,
          }}
        >
          <IonSpinner name="crescent" style={{ width: 40, height: 40 }} />
        </div>
      )}
      <IonCardHeader
        style={{ paddingBottom: 0, '--color': 'var(--ion-text-color)' }}
      >
        <HStack className="ion-justify-content-between">
          <IonCardTitle
            style={{ '--color': 'var(--ion-text-color)', whiteSpace: 'nowrap' }}
          >
            {device.entry!.name && device.entry!.name !== NAME_FREAK
              ? device.entry!.name
              : NAME_FREAK_DISPLAY}
          </IonCardTitle>
          <HStack className="ion-justify-content-end">
            <IonIcon slot="end" size="small" icon={bluetoothOutline} />
            <IonText style={{ marginLeft: 8, fontSize: 14, color: 'var(--ion-text-color)' }}>
              {isConnected
                ? 'CONNECTED'
                : isConnecting
                ? 'CONNECTING'
                : 'DISCONNECTED'}
            </IonText>
          </HStack>
        </HStack>
      </IonCardHeader>
      <IonCardContent>
        {firmwareLine()}
      </IonCardContent>
    </IonCard>
  );
});

export default SensorCard;
